/**
 * Location Service for Royale Chat
 * Provides functions for getting user location and reverse geocoding
 *
 * Uses a provider abstraction layer (Radar by default) for geocoding.
 * See src/services/location/providers/ for provider implementations.
 */

import { getLocationPermission, saveLocationPermission } from '../storage/userDataStorage';
import {
  getLocation as getLocationFromProvider,
  searchAutocomplete as searchAutocompleteFromProvider,
  reverseGeocode as reverseGeocodeFromProvider,
} from './providers';
import { createEmptyResponse } from './providers/types';

/**
 * Check if user has previously granted location permission
 * @returns {Promise<boolean>} True if permission was previously granted
 */
export const hasLocationPermission = async () => {
  const hasPermission = await getLocationPermission();
  return hasPermission;
};

/**
 * Reset location permission in storage
 * This allows us to prompt the user again if needed
 * @returns {Promise<boolean>} Success status
 */
export const resetLocationPermission = async () => {
  return await saveLocationPermission(false);
};

/**
 * Save user's location permission choice
 * @param {boolean} granted Whether permission was granted
 * @returns {Promise<boolean>} Success status
 */
export const setLocationPermission = async (granted) => {
  const success = await saveLocationPermission(granted);
  return success;
};

// --- GPS localStorage cache ---
// Survives backgrounding, page reloads, and app restarts.
// Only used as last resort — after high-accuracy GPS fails and before Wi-Fi/cell.
const GPS_CACHE_KEY = 'last_known_gps';
export const GPS_CACHE_MAX_AGE = 30 * 60 * 1000; // 30 minutes

function cacheGPSCoords(coords) {
  try {
    localStorage.setItem(
      GPS_CACHE_KEY,
      JSON.stringify({
        latitude: coords.latitude,
        longitude: coords.longitude,
        timestamp: Date.now(),
      })
    );
  } catch (e) {
    /* non-critical */
  }
}

function getCachedGPSCoords() {
  try {
    const cached = JSON.parse(localStorage.getItem(GPS_CACHE_KEY));
    if (cached && Date.now() - cached.timestamp < GPS_CACHE_MAX_AGE) {
      return { latitude: cached.latitude, longitude: cached.longitude };
    }
  } catch (e) {
    /* non-critical */
  }
  return null;
}

/**
 * Get the user's current location.
 *
 * Fallback chain (always prefer fresh high-accuracy data):
 * 1. High-accuracy GPS (satellite, maximumAge: 0, 10s timeout)
 * 2. localStorage cache (< 30 min old) — GPS-quality coords survive backgrounding
 * 3. Low-accuracy GPS (Wi-Fi/cell, maximumAge: 300000, 5s timeout)
 *
 * Exception: code 1 (PERMISSION_DENIED) rejects immediately — a denial is a
 * user decision, not a GPS hiccup. Substituting the cache made "Don't Allow"
 * register as granted (flag flipped, sharing on, recourse never shown).
 * The fallback chain applies only to codes 2/3 (unavailable/timeout).
 *
 * @returns {Promise<{latitude: number, longitude: number}>} Coordinates
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const start = Date.now();

    const onSuccess = (position, source) => {
      const accuracy = Math.round(position.coords.accuracy);
      const elapsed = Date.now() - start;
      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        _gpsTier: source,
        _gpsAccuracy: accuracy,
        _gpsElapsed: elapsed,
      };
      console.log(`[LOCATION] GPS fix (${source}): accuracy=${accuracy}m, elapsed=${elapsed}ms`);
      console.log(`[LOCATION] Caching GPS coords (accuracy=${accuracy}m)`);
      cacheGPSCoords(coords);
      resolve(coords);
    };

    // maximumAge: 0 — forces a real GPS query every time.
    // The browser cache doesn't distinguish accuracy levels. A stale Wi-Fi
    // position (~200m error) would satisfy maximumAge > 0 and skip the GPS
    // chip entirely, poisoning proximity detection (50m threshold).
    const highAccuracyOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    const lowAccuracyOptions = {
      enableHighAccuracy: false, // Wi-Fi/cell positioning
      timeout: 5000,
      maximumAge: 300000, // Accept cached here — already on degraded path
    };

    navigator.geolocation.getCurrentPosition(
      (position) => onSuccess(position, 'high-accuracy'),
      (highAccError) => {
        console.warn(
          `[LOCATION] High-accuracy GPS failed (code=${highAccError.code}, elapsed=${Date.now() - start}ms):`,
          highAccError.message
        );

        // PERMISSION_DENIED: never substitute the cache, and skip the
        // low-accuracy retry — the same permission governs it.
        if (highAccError.code === 1) {
          reject(highAccError);
          return;
        }

        // Check localStorage cache first — a cached GPS fix (~10m) is better
        // than a fresh Wi-Fi fix (~200m) for both proximity and venue detection
        const cached = getCachedGPSCoords();
        if (cached) {
          const raw = localStorage.getItem(GPS_CACHE_KEY);
          const cacheAge = raw ? Math.round((Date.now() - JSON.parse(raw).timestamp) / 60000) : '?';
          console.log(`[LOCATION] Using cached GPS coordinates (age: ${cacheAge} min)`);
          resolve({
            ...cached,
            _gpsTier: 'cache',
            _gpsElapsed: Date.now() - start,
          });
          return;
        }

        // No cache — last resort: Wi-Fi/cell positioning
        console.log('[LOCATION] No GPS cache, trying low-accuracy Wi-Fi/cell');
        navigator.geolocation.getCurrentPosition(
          (position) => onSuccess(position, 'low-accuracy'),
          (lowAccError) => {
            console.error(
              `[LOCATION] All GPS attempts failed (code=${lowAccError.code}, elapsed=${Date.now() - start}ms)`
            );
            reject(lowAccError);
          },
          lowAccuracyOptions
        );
      },
      highAccuracyOptions
    );
  });
};

/**
 * Convert coordinates to a human-readable address using the configured provider
 * @param {number} latitude Latitude
 * @param {number} longitude Longitude
 * @returns {Promise<import('./providers/types').StandardLocationResponse>} Location information
 */
export const reverseGeocode = async (latitude, longitude) => {
  try {
    // Use the provider abstraction (Radar by default)
    // Caching is handled by the provider
    const result = await getLocationFromProvider(latitude, longitude);
    return result;
  } catch (error) {
    console.error('[LOCATION] Reverse geocoding error:', error);
    throw error;
  }
};

/**
 * Build a clean, geocodable place label from a forward-search result's address.
 * Neighborhood → "Soho, New York"; city → "New York, NY" or "Paris, France".
 * Pure helper (unit-tested) so the label format stays stable as a stored hometown.
 *
 * @param {Object} address - address object from searchAutocomplete results
 * @returns {string} A clean place string, or '' if nothing usable
 */
export const buildPlaceLabel = (address) => {
  const a = address || {};
  if (a.neighborhood && a.city) return `${a.neighborhood}, ${a.city}`;
  if (a.city && a.stateCode) return `${a.city}, ${a.stateCode}`;
  if (a.city && a.country) return `${a.city}, ${a.country}`;
  return a.city || a.formattedAddress || a.neighborhood || '';
};

/**
 * Forward place search for the Hometown ("Current City") field and geocodeCity.
 * Returns clean, geocodable city/neighborhood places (not venues/addresses) by
 * restricting Radar to the locality + neighborhood layers.
 *
 * @param {string} query - Search text (city or neighborhood)
 * @returns {Promise<Array<{label: string, city: string|null, stateCode: string|null, countryCode: string|null, lat: number|null, lng: number|null}>>}
 */
export const searchPlaces = async (query) => {
  const results = await searchAutocompleteFromProvider(query, null, null, 'locality,neighborhood');
  return results
    .map((r) => ({
      label: buildPlaceLabel(r.address),
      city: r.address?.city || null,
      stateCode: r.address?.stateCode || null,
      countryCode: r.address?.countryCode || null,
      lat: r.latitude ?? null,
      lng: r.longitude ?? null,
    }))
    .filter((p) => p.label);
};

const GEOCODE_CACHE_KEY = 'geocode_city_cache';

/**
 * Geocode a place string (city or neighborhood) to a canonical
 * { city, countryCode, lat, lng }, cached in localStorage by the lowercased
 * string. Used by Nearby to place a connection's consent-gated shared hometown.
 * Returns null when the string can't be geocoded — failures are NOT cached, so
 * a transient API error retries on the next Nearby open. ~one call per unique
 * city per open, then free from cache.
 *
 * @param {string} place - The shared hometown / place string
 * @returns {Promise<{city: string|null, countryCode: string|null, lat: number|null, lng: number|null}|null>}
 */
export const geocodeCity = async (place) => {
  const key = (place || '').trim().toLowerCase();
  if (!key) return null;

  let cache = {};
  try {
    cache = JSON.parse(localStorage.getItem(GEOCODE_CACHE_KEY) || '{}');
  } catch (e) {
    cache = {};
  }
  if (Object.prototype.hasOwnProperty.call(cache, key)) {
    return cache[key];
  }

  // Cache miss → forward search, take the best (first) match
  const places = await searchPlaces(place);
  if (!places.length) return null; // don't cache failures — may be transient

  const { city, countryCode, lat, lng } = places[0];
  const result = { city, countryCode, lat, lng };

  try {
    cache[key] = result;
    localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    /* non-critical */
  }

  return result;
};

const REVERSE_GEOCODE_CACHE_KEY = 'reverse_geocode_city_cache';

/**
 * Reverse-geocode coordinates to a canonical { city, countryCode }, cached in
 * localStorage on a ~0.7mi grid (2-decimal coords). Used by Nearby for the LIVE
 * path: live rows store an abbreviated city ("LA", "LV") that forward-geocodes
 * wrong (LA→Laredo, LV→Lviv), so we resolve the city from the authoritative
 * coordinates instead. Returns null when reverse geocoding fails (not cached).
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{city: string|null, countryCode: string|null}|null>}
 */
export const reverseGeocodeCity = async (lat, lng) => {
  if (lat == null || lng == null) return null;
  const key = `${lat.toFixed(2)},${lng.toFixed(2)}`;

  let cache = {};
  try {
    cache = JSON.parse(localStorage.getItem(REVERSE_GEOCODE_CACHE_KEY) || '{}');
  } catch (e) {
    cache = {};
  }
  if (Object.prototype.hasOwnProperty.call(cache, key)) {
    return cache[key];
  }

  const addr = await reverseGeocodeFromProvider(lat, lng);
  if (!addr || !addr.city) return null; // don't cache failures

  const result = { city: addr.city, countryCode: addr.countryCode || null };
  try {
    cache[key] = result;
    localStorage.setItem(REVERSE_GEOCODE_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    /* non-critical */
  }

  return result;
};

/**
 * Check if location data has a usable venue or address (not low-confidence)
 * Used to guard formatLocationForDisplay — only format if there's something meaningful to show.
 * City counts: the coarse reverse-geocode fallback returns city-only results
 * for address-less coordinates, and a city is meaningful to display.
 *
 * @param {Object} locationData - Location data from getLocationWithVenue
 * @returns {boolean} True if data has a venue name, street, neighborhood, or city
 */
export const hasVenueOrAddress = (locationData) => {
  const venueInfo = locationData?.venueInfo;
  if (!venueInfo) return false;
  if (venueInfo.confidence === 'low') return false;
  return !!(
    venueInfo.venue?.name ||
    venueInfo.address?.street ||
    venueInfo.address?.neighborhood ||
    venueInfo.address?.city
  );
};

/**
 * Format location data into a human-readable string
 * Supports both new StandardLocationResponse format and legacy format
 *
 * @param {Object} locationData - Location data from getLocationWithVenue
 * @returns {string} - Formatted location string
 */
export const formatLocationForDisplay = (locationData) => {
  if (!locationData) {
    return '';
  }

  try {
    // Check if this is the new format (has venueInfo with provider field)
    // New format: { coords, venueInfo: StandardLocationResponse }
    const venueInfo = locationData.venueInfo;

    if (venueInfo && venueInfo.provider) {
      // NEW FORMAT: StandardLocationResponse
      const { venue, address } = venueInfo;

      const components = [];

      // Venue name
      if (venue?.name) {
        components.push(venue.name);
      }

      // Street address (number + street)
      if (address?.streetNumber && address?.street) {
        components.push(`${address.streetNumber} ${address.street}`);
      } else if (address?.street) {
        components.push(address.street);
      }

      // Neighborhood
      if (address?.neighborhood) {
        components.push(address.neighborhood);
      }

      // City (abbreviated)
      if (address?.cityAbbr) {
        components.push(address.cityAbbr);
      } else if (address?.city) {
        components.push(address.city);
      }

      if (components.length > 0) {
        return components.join(', ');
      }

      // Fallback to formatted address from provider
      if (address?.formattedAddress) {
        return address.formattedAddress;
      }
    }

    // No structured venue data — fall back to coordinates
    if (locationData.coords) {
      // Fall back to coordinates if nothing else
      return `${locationData.coords.latitude.toFixed(4)}, ${locationData.coords.longitude.toFixed(4)}`;
    }

    return '';
  } catch (error) {
    console.error('[LOCATION] Error formatting location:', error);
    return '';
  }
};

export const getLocationWithVenue = async (existingCoords = null) => {
  try {
    const coords = existingCoords || (await getCurrentLocation());

    try {
      // Try to get venue info but don't fail if it doesn't work
      // reverseGeocode now returns StandardLocationResponse from the provider
      const venueInfo = await reverseGeocode(coords.latitude, coords.longitude);
      return {
        coords,
        venueInfo,
      };
    } catch (geocodeError) {
      console.error(
        '[LOCATION] Error with geocoding, continuing with coordinates only:',
        geocodeError
      );
      // Return coordinates only — formatters handle missing fields gracefully
      return {
        coords,
        venueInfo: createEmptyResponse(coords, 'radar'),
      };
    }
  } catch (error) {
    console.error('[LOCATION] Error getting location with venue:', error);
    throw error;
  }
};
